import { useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loading } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { IconArrowBack } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { BasketItem } from '@/models/shop.ts'
import { AlbumFile } from '@/models/albumFile.ts'
import { CropData } from '@/types/photoCrop.ts'
import { PhotoCropHeader } from '@/components/shop/PhotoCropHeader.tsx'
import { PhotoCropList } from '@/components/shop/PhotoCropList.tsx'
import { PhotoCropBottomActions } from '@/components/shop/PhotoCropBottomActions.tsx'
import { CropConfirmModal, useCropConfirmModal } from '@/components/shop/CropConfirmModal.tsx'

export default function BasketEditCropPhotosPage() {
  const { driveId, orderItemId } = useParams<{ driveId: string; orderItemId: string }>()
  const navigate = useNavigate()
  const { getBasketItemDetail, updateBasketItemDetails } = useClient()

  const [basketItem, setBasketItem] = useState<BasketItem | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedImages, setSelectedImages] = useState<AlbumFile[]>([])
  const [cropData, setCropData] = useState<CropData>({})
  const [isProcessing, setIsProcessing] = useState(false)
  const { modalRef, show: showConfirmModal } = useCropConfirmModal()

  useEffect(() => {
    const fetchBasketItem = async () => {
      if (!orderItemId) return

      try {
        setIsLoading(true)
        const response = await getBasketItemDetail(orderItemId)
        const item: BasketItem = response.data
        setBasketItem(item)

        if (item.playlist?.files) {
          const images = item.playlist.files.filter(con => con.file).map(connection => connection.file!)
          setSelectedImages(images)

          // Load existing crop data as percentages
          const initialCropData: CropData = {}
          item.playlist.files.filter(file => file.file).forEach(connection => {
            const cropX = connection.crop_x || 0
            const cropY = connection.crop_y || 0
            const cropWidth = connection.crop_width || 100
            const cropHeight = connection.crop_height || 100

            initialCropData[connection.file!.id] = {
              crop: {
                x: cropX,
                y: cropY,
                width: cropWidth,
                height: cropHeight
              },
              count: connection.count || 1,
              rotated: connection.crop_rotated || false
            }
          })
          setCropData(initialCropData)
        }
      } catch (error) {
        console.error('Error fetching basket item:', error)
        setError('Fehler beim Laden des Warenkorb-Artikels')
      } finally {
        setIsLoading(false)
      }
    }

    fetchBasketItem()
  }, [orderItemId, getBasketItemDetail])

  const getAspectRatio = (rotated: boolean = false) => {
    if (!basketItem?.variation) {
      throw new Error('No variation found - cannot determine aspect ratio')
    }

    const widthProperty = basketItem.variation.property_value_links.find(prop => prop.property_value.width_mm != null)
    const heightProperty = basketItem.variation.property_value_links.find(prop => prop.property_value.height_mm != null)

    if (!widthProperty || !heightProperty) {
      throw new Error('No width/height properties found in variation')
    }

    const width = widthProperty.property_value.width_mm!
    const height = heightProperty.property_value.height_mm!

    return rotated ? height / width : width / height
  }

  // Check if rotation is disabled for any property value or if dimensions are square
  const isRotationDisabled = basketItem?.variation?.property_value_links.some(
    prop => prop.property_value.disable_rotation === true ||
            (prop.property_value.width_mm != null &&
             prop.property_value.height_mm != null &&
             prop.property_value.width_mm === prop.property_value.height_mm)
  ) ?? false

  const updateCrop = (imageId: string, crop: { x: number; y: number; width: number; height: number }) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        crop
      }
    }))
  }

  const updateCount = (imageId: string, delta: number) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        count: Math.max(1, prev[imageId].count + delta)
      }
    }))
  }

  const toggleRotation = (imageId: string) => {
    setCropData(prev => ({
      ...prev,
      [imageId]: {
        ...prev[imageId],
        rotated: !prev[imageId].rotated
      }
    }))
  }

  const removeImage = (imageId: string) => {
    setSelectedImages(prev => prev.filter(img => img.id !== imageId))
    setCropData(prev => {
      const newData = { ...prev }
      delete newData[imageId]
      return newData
    })
  }

  const handleSaveChanges = async () => {
    if (!basketItem) return

    setIsProcessing(true)

    try {
      const fileIds = selectedImages.map(img => img.id)

      // Convert cropData to the format expected by the API (percentages)
      const cropDataForAPI: Record<string, any> = {}
      selectedImages.forEach(image => {
        const imageCropData = cropData[image.id]
        if (imageCropData) {
          cropDataForAPI[image.id] = {
            crop_x: imageCropData.crop.x,           // Percentage (0-100)
            crop_y: imageCropData.crop.y,           // Percentage (0-100)
            crop_width: imageCropData.crop.width,   // Percentage (0-100)
            crop_height: imageCropData.crop.height, // Percentage (0-100)
            crop_rotated: imageCropData.rotated,
            count: imageCropData.count
          }
        }
      })

      // Debug log to verify crop values are percentages (0-100)
      console.log('Crop data being sent to API:', cropDataForAPI)


      await updateBasketItemDetails(orderItemId!, {
        files: fileIds,
        crop_data: cropDataForAPI,
        update_existing: true
      })

      navigate(`/d/${driveId}/shop/basket`)
    } catch (error) {
      console.error('Error updating basket item:', error)
      alert('Fehler beim Speichern der Änderungen')
    } finally {
      setIsProcessing(false)
    }
  }

  const getTotalImageCount = () => {
    return Object.values(cropData).reduce((total, data) => total + data.count, 0)
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Loading color="primary" />
      </div>
    )
  }

  if (error || !basketItem) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">
            {error || 'Warenkorb-Artikel nicht gefunden'}
          </h1>
          <Button color="primary" variant="outline" onClick={() => navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}`)}>
            <IconArrowBack size={20} />
            Zurück
          </Button>
        </div>
      </div>
    )
  }

  if (selectedImages.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
          <h1 className="text-2xl font-bold text-error">Keine Bilder ausgewählt</h1>
          <Button
            color="primary"
            variant="outline"
            onClick={() => navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}/select-photos`)}
          >
            <IconArrowBack size={20} />
            Zurück zur Bildauswahl
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <PhotoCropHeader
        title="Zuschnitt bearbeiten"
        subtitle={basketItem.product.title}
        selectedImagesCount={selectedImages.length}
        totalPrintCount={getTotalImageCount()}
      />

      <PhotoCropList
        selectedImages={selectedImages}
        cropData={cropData}
        getAspectRatio={getAspectRatio}
        updateCrop={updateCrop}
        updateCount={updateCount}
        toggleRotation={toggleRotation}
        removeImage={removeImage}
        isEditingExisting={true}
        disableRotation={isRotationDisabled}
      />

      <CropConfirmModal
        modalRef={modalRef}
        onConfirm={handleSaveChanges}
        isLoading={isProcessing}
      />

      <PhotoCropBottomActions
        onBack={() => navigate(`/d/${driveId}/shop/basket/edit/${orderItemId}/select-photos`)}
        onContinue={showConfirmModal}
        isProcessing={isProcessing}
        disabled={isProcessing || selectedImages.length === 0}
        continueText="Speichern"
        totalCount={getTotalImageCount()}
      />
    </div>
  )
}
